import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Check, FileText, Plus, Trash2, ChevronUp, ChevronDown, Save, X,
  ArrowRightLeft, Pencil } from "lucide-react";
import DocumentoDialog from "@/components/GeneraDocumentoDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import PraticaDocsBox, { ZonaCaricamento } from "@/components/PraticaDocsBox";
import ComunicazioniBox from "@/components/ComunicazioniBox";
import ReferenteSelect from "@/components/ReferenteSelect";
import PraticaDenaro from "@/components/PraticaDenaro";
import PraticaPagamenti from "@/components/PraticaPagamenti";
import TrasformaPraticaDialog from "@/components/TrasformaPraticaDialog";
import CambiaTipoDialog from "@/components/CambiaTipoDialog";
import PraticaCampi from "@/components/PraticaCampi";
import BloccoAltro from "@/components/BloccoAltro";
import ChecklistDocumenti from "@/components/ChecklistDocumenti";
import DocumentiGenerati from "@/components/DocumentiGenerati";
import CompitiPratica from "@/components/CompitiPratica";
import Sezione from "@/components/Sezione";
import AzioniPratica from "@/components/AzioniPratica";
import PraticaDiario from "@/components/PraticaDiario";
import { useAuth } from "@/context/AuthContext";
import { eur } from "@/lib/format";
import StatoBadge from "@/components/StatoBadge";

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

// Nel titolo il nome della pratica si legge come una frase: maiuscola solo all'inizio,
// niente parentesi. Negli elenchi resta la forma del tipo pratica.
const nomePratica = (s) => {
  if (!s || !s.trim()) return "Pratica";
  const originale = s.trim();
  let t = originale.replace(/\s*\(([^)]+)\)/g, (m, g) => ` · ${g.toLowerCase()}`);
  const lettere = originale.replace(/[^A-Za-zÀ-ÿ]/g, "");
  const maiuscole = (originale.match(/[A-ZÀ-Þ]/g) || []).length;
  if (lettere.length > 3 && maiuscole / lettere.length > 0.7) t = t.toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
};


export default function PraticaDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [azione, setAzione] = useState(null);
  const [diarioApri, setDiarioApri] = useState(null);
  const { data: p } = useQuery({ queryKey: ["pratica", id], queryFn: async () => (await api.get(`/pratiche/${id}`)).data });

  const [steps, setSteps] = useState([]);
  const [stepsDirty, setStepsDirty] = useState(false);
  const [apriStep, setApriStep] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [emailDoc, setEmailDoc] = useState(null);
  const [emailForm, setEmailForm] = useState({ oggetto: "", testo: "" });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editRif, setEditRif] = useState(false);
  const [trasformaOpen, setTrasformaOpen] = useState(false);
  const [cambiaTipoOpen, setCambiaTipoOpen] = useState(false);
  const [motivoArch, setMotivoArch] = useState("");

  useEffect(() => { if (p) { setSteps((p.steps || []).map((s) => ({ ...s, _k: uid() }))); } }, [p]);

  // Arrivo dalla scheda cliente con un'azione contabile da aprire subito
  useEffect(() => {
    const a = params.get("azione");
    if (["incasso", "spesa", "ricevuta"].includes(a)) {
      setAzione(a);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  if (!p) return <div className="text-sm text-[#4a5568]">Caricamento…</div>;
  const fatti = steps.filter((s2) => s2.completato).length;
  const perc = steps.length ? Math.round((fatti / steps.length) * 100) : 0;
  const corrente = steps.find((s) => !s.completato);
  const refresh = () => qc.invalidateQueries({ queryKey: ["pratica", id] });
  const docMancanti = (p.documenti_stato || []).filter((d) => !d.presente && (d.obbligatorio || d.mancante_segnato));

  const saveRif = async (rid) => {
    try {
      await api.patch(`/clienti/${p.cliente_id}/anagrafica`, { referente_id: rid || null });
      toast.success("Rif. aggiornato"); setEditRif(false);
      qc.invalidateQueries({ queryKey: ["pratica", id] });
      qc.invalidateQueries({ queryKey: ["clienti"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore aggiornamento Rif."); }
  };

  // ---- Step ----
  const CHIUSURE = ["archiviata", "non proseguita"];
  const setStep = (i, patch) => {
    if (patch.completato && CHIUSURE.includes((steps[i]?.nome || "").trim().toLowerCase()) && !p.motivo_archiviazione) {
      const m = window.prompt("Motivo dell'archiviazione (rinuncia cliente · mancato pagamento · esito negativo · rinvio a legale · altro):", "");
      if (!m || !m.trim()) return toast.warning("Archiviazione annullata: serve il motivo");
      setMotivoArch(m.trim());
    }
    setSteps(steps.map((s, j) => j === i ? { ...s, ...patch } : s)); setStepsDirty(true);
  };
  const moveStep = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= steps.length) return;
    const copy = [...steps]; [copy[i], copy[j]] = [copy[j], copy[i]]; setSteps(copy); setStepsDirty(true);
  };
  const addStep = () => { setSteps([...steps, { _k: uid(), nome: "Nuovo step", completato: false, descrizione: "" }]); setStepsDirty(true); };
  const insertStep = (i) => { const copy = [...steps]; copy.splice(i + 1, 0, { _k: uid(), nome: "Nuovo step", completato: false, descrizione: "" }); setSteps(copy); setStepsDirty(true); };
  const removeStep = (i) => { setSteps(steps.filter((_, j) => j !== i)); setStepsDirty(true); };
  const saveSteps = async () => {
    try {
      await api.put(`/pratiche/${id}/steps`, { steps: steps.map(({ _k, ...s }) => s), motivo_archiviazione: motivoArch });
      setStepsDirty(false); refresh(); toast.success("Step salvati");
    } catch { toast.warning("Salvataggio step non riuscito"); }
  };

  const toggleAttiva = async () => { try { await api.put(`/pratiche/${id}/dettagli`, { attiva: !p.attiva }); refresh(); qc.invalidateQueries({ queryKey: ["pratiche"] }); } catch { toast.warning("Operazione non riuscita"); } };
  const togglePriorita = async () => { try { await api.put(`/pratiche/${id}/dettagli`, { priorita: !p.priorita }); refresh(); qc.invalidateQueries({ queryKey: ["pratiche"] }); toast.success(!p.priorita ? "Aggiunta alle priorità" : "Rimossa dalle priorità"); } catch { toast.warning("Operazione non riuscita"); } };
  const attivaSponsor = async () => {
    try {
      if (!p.ha_sponsor) await api.put(`/pratiche/${id}/dettagli`, { ha_sponsor: true });
      refresh();
      toast.success(p.ha_sponsor ? "Compila i campi dello sponsor in «Altro»" : "Sezione sponsor attivata in «Altro»");
      setTimeout(() => document.querySelector('[data-testid="sez-altro"]')?.scrollIntoView({ behavior: "smooth" }), 400);
    } catch { toast.error("Operazione non riuscita"); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/pratiche/${id}`);
      toast.success("Pratica spostata nel cestino");
      qc.invalidateQueries({ queryKey: ["pratiche"] });
      navigate("/pratiche");
    } catch (e) { toast.error(e?.response?.data?.detail || "Eliminazione non riuscita"); }
    finally { setDeleteOpen(false); }
  };

  const docUrl = (docId) => `${api.defaults.baseURL}/documenti/${docId}/download?token=${localStorage.getItem("rb_token")}`;

  const openEmail = (d) => {
    setEmailForm({ oggetto: d.label || "Documento RB Studio",
      testo: `Buongiorno ${p.cliente?.nome || ""},\n\nin allegato trova ${d.label || "il documento"}.\nRestiamo a disposizione per qualsiasi chiarimento.\n\nCordiali saluti,\nRB Studio` });
    setEmailDoc(d);
  };
  const sendEmailDraft = async () => {
    try {
      const { data } = await api.post(`/documenti/${emailDoc.id}/email-draft`, emailForm);
      toast.success("Bozza email creata in Gmail (da rivedere e inviare manualmente).");
      if (data.draft_link) window.open(data.draft_link, "_blank");
      setEmailDoc(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione bozza"); }
  };

  const generaBtn = (
    <button data-testid="gen-documento-btn" onClick={() => setDocOpen(true)}
      className="btn btn-g text-[15px] px-[22px]">Genera documento</button>
  );

  return (
    <div data-testid="pratica-detail-page"
      className="scheda-rb -m-4 sm:-m-5 lg:-m-7 p-4 sm:p-5 lg:p-7 bg-[#F2F1EC] min-h-screen">
      <div className="w-full max-w-[1120px] mx-auto flex flex-col gap-[18px]">
      <Link to="/pratiche" className="text-[14px] font-semibold no-underline">← Pratiche</Link>

      {/* 0 · Intestazione: prima la pratica, poi il cliente, stessa dimensione */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 data-testid="pratica-nome" className="text-[24px] md:text-[30px] font-bold leading-[1.15] text-[#1B2A4B] break-words">
            {nomePratica(p.tipo_pratica_nome)}
            {p.titolo && p.titolo.trim()
              && !p.titolo.toLowerCase().includes((p.tipo_pratica_nome || "·").toLowerCase()) && (
              <span className="text-[#4a5568] italic font-normal"> «{p.titolo}»</span>
            )}
          </h1>
          {p.cliente && (
            <Link data-testid="pratica-cliente-name-link" to={`/clienti/${p.cliente_id}`}
              className="block text-[22px] md:text-[24px] font-normal leading-[1.15] text-[#4A4A48] break-words text-left
                         no-underline hover:text-[#1B2A4B] transition-colors">
              {p.cliente.nome} {p.cliente.cognome}
            </Link>
          )}
          {editRif && p.cliente && null}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatoBadge stato={p.stato_derivato || "Da avviare"} testid="stato-corrente"
            className="text-[13px] font-bold px-3 py-1.5 rounded-[3px]" />
          {p.priorita && <span className="text-[13px] font-bold text-[#8a6d1f] bg-[#F5EBCF] rounded-[3px] px-3 py-1.5">Prioritaria</span>}
          {!p.attiva && <span data-testid="etichetta-in-pausa" className="text-[13px] text-[#4A4A48] bg-[#ECEAE3] rounded-[3px] px-3 py-1.5">In pausa</span>}
          {p.ha_sponsor && <span data-testid="etichetta-sponsor" className="text-[13px] text-[#4A4A48] bg-[#ECEAE3] rounded-[3px] px-3 py-1.5">Con sponsor</span>}
          {docMancanti.length > 0 && (
            <span data-testid="badge-documenti-mancanti" title={docMancanti.map((d) => d.nome).join(" · ")}
              className="text-[13px] font-bold text-[#9A3B13] bg-[#FDF6F1] rounded-[3px] px-3 py-1.5">
              {docMancanti.length} mancanti
            </span>
          )}
          {p.cliente && (
            <span data-testid="pratica-rif" className="text-[13px] text-[#4A4A48] bg-[#ECEAE3] rounded-[3px] px-3 py-1.5 inline-flex items-center gap-1.5">
              Rif. {p.cliente.referente ? [p.cliente.referente.cognome, p.cliente.referente.nome].filter(Boolean).join(" ") : "RB Studio"}
              <button data-testid="pratica-rif-edit" onClick={() => setEditRif(!editRif)} className="text-[#5A5A5A] hover:text-[#1B2A4B]"><Pencil className="w-3 h-3" /></button>
            </span>
          )}
        <AzioniPratica pratica={p} puoEliminare={can("elimina_pratiche")}
          onCambiaTipo={() => setCambiaTipoOpen(true)} onTrasforma={() => setTrasformaOpen(true)}
          onPriorita={togglePriorita} onAttiva={toggleAttiva} onSponsor={attivaSponsor}
          onElimina={() => setDeleteOpen(true)} />
        </div>
      </header>
      {editRif && p.cliente && (
        <div className="sez p-4 max-w-md">
          <ReferenteSelect value={p.cliente.referente_id || ""} onChange={saveRif} testid="pratica-rif-select" />
          <button data-testid="pratica-rif-close" onClick={() => setEditRif(false)} className="text-[13px] mt-2 font-semibold">Chiudi</button>
        </div>
      )}

      {p.nasce_da && (
        <div data-testid="pratica-origine-banner" className="mb-4 rounded-lg border border-[#C9A84C]/40 bg-[#fbf9f3] px-4 py-2.5 text-sm text-[#1A2B4A]">
          <ArrowRightLeft className="w-3.5 h-3.5 inline mr-1.5 text-[#8a6d1f]" />
          Nasce da <b>{p.nasce_da.tipo}</b> · {p.nasce_da.data}
          {(p.origine_pratica?.importo_scalato > 0) && <> · {eur(p.origine_pratica.importo_scalato)} scalati come acconto</>}
          {" · "}<Link data-testid="pratica-origine-link" to={`/pratiche/${p.nasce_da.id}`} className="underline decoration-[#C9A84C]/50 hover:text-[#C9A84C]">apri l'origine</Link>
        </div>
      )}
      {p.trasformata_in && (
        <div data-testid="pratica-trasformata-banner" className="mb-4 rounded-lg border border-[#1A2B4A]/15 bg-[#f9f8f5] px-4 py-2.5 text-sm text-[#1A2B4A]">
          Trasformata in <b>{p.trasformata_in.tipo}</b> il {p.trasformata_in.data} ·{" "}
          <Link data-testid="pratica-trasformata-link" to={`/pratiche/${p.trasformata_in.id}`} className="underline decoration-[#C9A84C]/50 hover:text-[#C9A84C]">apri la nuova pratica</Link>
        </div>
      )}
      {(p.ha_generato || []).length > 0 && (
        <div data-testid="pratica-generate-banner" className="mb-4 rounded-lg border border-[#1A2B4A]/15 bg-[#f9f8f5] px-4 py-2.5 text-sm text-[#1A2B4A]">
          Ha generato: {p.ha_generato.map((g) => (
            <Link key={g.id} data-testid={`pratica-generata-${g.id}`} to={`/pratiche/${g.id}`}
              className="underline decoration-[#C9A84C]/50 hover:text-[#C9A84C] mr-2">{g.tipo} · {g.data}</Link>
          ))}
        </div>
      )}

      <>
        {/* 1 · Avanzamento: una riga sola, gli step si aprono a richiesta */}
        <div data-testid="sez-avanzamento" className="sez px-5 py-3.5 flex flex-wrap items-center gap-4">
          <span className="eti shrink-0">Avanzamento</span>
          <span data-testid="avanzamento-conteggio" className="text-[16px] font-bold text-[#1B2A4B] shrink-0">
            {fatti} di {steps.length} · {perc}%
          </span>
          <span className="flex-[999_1_240px] min-w-0 h-2 bg-[#E8E6DF] rounded-[4px] overflow-hidden block">
            <span data-testid="avanzamento-barra" className="block h-2 bg-[#A8863F] transition-[width] duration-700 ease-out" style={{ width: `${perc}%` }} />
          </span>
          <span data-testid="avanzamento-step-corrente" className="text-[14px] text-[#4A4A48] shrink-0 max-w-full truncate">
            {corrente ? <>In corso: <strong className="font-semibold">{corrente.nome}</strong></> : "tutti gli step completati"}
          </span>
          <button data-testid="apri-step" onClick={() => setApriStep(!apriStep)}
            className="btn btn-o btn-piccolo shrink-0 btn-larghezza">
            {apriStep ? "Chiudi gli step" : `Apri i ${steps.length} step`}
          </button>
          {apriStep && (
            <div className="w-full pt-3 border-t border-[#F0EEE8]">
              <div className="flex items-center gap-2 mb-3">
                <button data-testid="add-step" onClick={addStep} className="btn btn-o btn-mini"><Plus className="w-3.5 h-3.5" /> Step</button>
                {stepsDirty && <button data-testid="save-steps" onClick={saveSteps} className="btn btn-p btn-mini"><Save className="w-3.5 h-3.5" /> Salva</button>}
              </div>
              <ol className="flex flex-col">
                {steps.map((s2, i2) => {
                  const attuale = !s2.completato && steps.findIndex((x) => !x.completato) === i2;
                  return (
                    <li key={s2._k} data-testid={`step-row-${i2}`} className="riga group gap-3">
                      <button data-testid={`step-toggle-${i2}`} onClick={() => setStep(i2, { completato: !s2.completato })}
                        className={`shrink-0 w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold transition-colors
                          ${s2.completato ? "bg-[#A8863F] text-white" : attuale ? "bg-white text-[#A8863F] border-2 border-[#A8863F]" : "bg-white text-[#5A5A5A] border border-[#D8D5CC]"}`}>
                        {s2.completato ? <Check className="w-3.5 h-3.5" /> : i2 + 1}
                      </button>
                      <input data-testid={`step-name-${i2}`} value={s2.nome} onChange={(e) => setStep(i2, { nome: e.target.value })}
                        className={`flex-grow min-w-0 px-2 py-1 text-[14px] bg-transparent border border-transparent hover:border-[#E2E0D9] focus:border-[#A8863F] rounded-[4px] focus:outline-none
                          ${s2.completato ? "line-through text-[#5A5A5A]" : "text-[#1A1A1A]"}`} />
                      <span className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <button data-testid={`step-insert-${i2}`} onClick={() => insertStep(i2)} title="Inserisci qui sotto" className="text-[#A8863F]"><Plus className="w-4 h-4" /></button>
                        <button onClick={() => moveStep(i2, -1)} title="Su" className="text-[#5A5A5A]"><ChevronUp className="w-4 h-4" /></button>
                        <button onClick={() => moveStep(i2, 1)} title="Giù" className="text-[#5A5A5A]"><ChevronDown className="w-4 h-4" /></button>
                        <button data-testid={`step-remove-${i2}`} onClick={() => removeStep(i2)} title="Elimina" className="text-[#8A2E2E]"><Trash2 className="w-3.5 h-3.5" /></button>
                      </span>
                    </li>
                  );
                })}
                {steps.length === 0 && <li className="riga text-[#5A5A5A]">Nessuno step. Aggiungine uno.</li>}
              </ol>
            </div>
          )}
        </div>

        {/* 2 · Documenti: caricamento, caricati, generati e checklist in una sezione sola */}
        <Sezione testid="sez-documenti" titolo="Documenti" azioni={
          <>
            {docMancanti.length > 0 && (
              <span className="text-[13px] text-[#E7D9B2] md:hidden">{docMancanti.length} mancanti</span>
            )}
            <span className="hidden md:block">{generaBtn}</span>
          </>}>
          {p.cliente ? (
            <>
              <div className="flex flex-col gap-2.5 md:hidden">
                <button data-testid="gen-documento-btn-mobile" onClick={() => setDocOpen(true)}
                  className="btn btn-g btn-larghezza">Genera documento</button>
                <ZonaCaricamento cliente={p.cliente} soloBottone />
              </div>
              <div className="hidden md:block"><ZonaCaricamento cliente={p.cliente} /></div>
              <div className="sub grid md:grid-cols-2 min-w-0" data-testid="box-documenti">
                <div className="min-w-0 border-b md:border-b-0 md:border-r border-[#E8E6DF]">
                  <PraticaDocsBox cliente={p.cliente} documentiGenerati={p.documenti_generati || []} compatto />
                </div>
                <div className="min-w-0" data-testid="box-documenti-generati">
                  <DocumentiGenerati clienteId={p.cliente_id} praticaId={p.id} titolo="Contratti e atti generati"
                    nota="Qui finisce tutto ciò che esce da «Genera documento»." />
                </div>
              </div>
            </>
          ) : (
            <p className="text-[14px] text-[#5A5A5A]">Cliente non collegato alla pratica.</p>
          )}
          <ChecklistDocumenti pratica={p} onUploaded={refresh} />
        </Sezione>

        {/* 3 · Contabilità */}
        <Sezione testid="sez-contabilita" titolo="Contabilità">
          <PraticaDenaro pratica={p} onChanged={refresh} senzaCornice
            azione={azione} onAzioneFatta={() => setAzione(null)}>
            <PraticaPagamenti pratica={p} embedded />
          </PraticaDenaro>
        </Sezione>

        {/* 4 · Diario della pratica */}
        <Sezione testid="sez-diario" titolo="Diario della pratica" azioni={
          <>
            <button data-testid="diario-nuova-nota" onClick={() => setDiarioApri(diarioApri === "nota" ? null : "nota")}
              className="btn btn-g btn-larghezza sm:w-auto">Nota</button>
            <button data-testid="diario-nuovo-evento" onClick={() => setDiarioApri(diarioApri === "evento" ? null : "evento")}
              className="btn btn-o btn-larghezza sm:w-auto">Evento in agenda</button>
          </>}>
          <PraticaDiario pratica={p} onChanged={refresh} apri={diarioApri} setApri={setDiarioApri} />
        </Sezione>

        {/* 5 · Altro */}
        <Sezione testid="sez-altro" titolo="Altro" aperta={false}>
          <div className="flex flex-col gap-[18px]">
            {p.cliente && (
              <BloccoAltro titolo="Comunicazioni al cliente" testid="blocco-comunicazioni">
                <div className="p-3.5"><ComunicazioniBox cliente={p.cliente} praticaId={p.id} senzaCornice /></div>
              </BloccoAltro>
            )}
            <BloccoAltro titolo="Compiti" testid="blocco-compiti">
              <div className="p-3.5"><CompitiPratica pratica={p} senzaCornice /></div>
            </BloccoAltro>
            <BloccoAltro testid="blocco-sponsor"
              titolo={p.ha_sponsor ? "Sponsor e dati della pratica" : "Dati della pratica"}
              nota={p.ha_sponsor ? null : "lo sponsor compare solo se dichiarato all'apertura"}>
              <div className="p-3.5"><PraticaCampi pratica={p} onChanged={refresh} senzaCornice /></div>
            </BloccoAltro>
            <details data-testid="storico-immutabile" className="sub">
              <summary className="subt cursor-pointer list-none">
                <span>Storico</span>
                <span className="text-[13px] font-semibold text-[#8A6E2F]">Mostra</span>
              </summary>
              <div className="nota-tenue border-t-0">
                Registro immutabile di ogni operazione, cancellazioni comprese. Chiuso per non occupare spazio.
              </div>
              <ul className="max-h-72 overflow-y-auto">
                {[...(p.storico || [])].reverse().map((h, i) => (
                  <li key={`${h.timestamp}-${i}`} className="riga gap-3 items-start">
                    <span className="w-[104px] shrink-0 text-[13px] font-bold text-[#1B2A4B]">{new Date(h.timestamp).toLocaleDateString("it-IT")}</span>
                    <span className="flex-grow text-[14px]">{h.testo}</span>
                  </li>
                ))}
              </ul>
            </details>
            {can("elimina_pratiche") && (
              <div className="flex flex-wrap items-center gap-3.5 border-t border-[#E8E6DF] pt-4">
                <span className="text-[13px] text-[#5A5A5A] flex-[999_1_320px]">
                  Eliminare la pratica la porta nel cestino per 30 giorni. Cliente e documenti restano.
                </span>
                <button data-testid="delete-pratica-btn" onClick={() => setDeleteOpen(true)} className="btn btn-r btn-larghezza">
                  Elimina pratica
                </button>
              </div>
            )}
          </div>
        </Sezione>
      </>
      </div>

      {docOpen && (
        <DocumentoDialog open={docOpen} onClose={() => setDocOpen(false)}
          clienteId={p.cliente_id} clienteNome={p.cliente ? `${p.cliente.nome} ${p.cliente.cognome}` : ""}
          praticaId={id} onGenerated={() => refresh()} />
      )}

      {emailDoc && (
        <div className="fixed inset-0 z-50 bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-4 border-t-2 border-[#C9A84C] shadow-lg" data-testid="email-dialog">
            <div className="flex items-start justify-between">
              <h3 className="font-serif text-2xl text-[#1A2B4A] mb-1">Bozza email</h3>
              <button onClick={() => setEmailDoc(null)} className="text-[#1A2B4A] hover:text-[#9b2c2c]"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-[#4a5568] mb-4">Verrà creata una <b>bozza</b> in Gmail con il PDF allegato. Nessun invio automatico.</p>
            <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Oggetto</label>
            <input data-testid="email-oggetto" value={emailForm.oggetto} onChange={(e) => setEmailForm({ ...emailForm, oggetto: e.target.value })}
              className="w-full mt-1 mb-4 px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
            <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Testo</label>
            <textarea data-testid="email-testo" rows={6} value={emailForm.testo} onChange={(e) => setEmailForm({ ...emailForm, testo: e.target.value })}
              className="w-full mt-1 mb-6 px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C] resize-none" />
            <div className="flex justify-end gap-3">
              <button onClick={() => setEmailDoc(null)} className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-sm hover:bg-[#f9f8f5]">Annulla</button>
              <button data-testid="email-create" onClick={sendEmailDraft} className="px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-sm hover:bg-[#22345c]">Crea bozza</button>
            </div>
          </div>
        </div>
      )}
      {deleteOpen && (
        <ConfirmDialog title="Spostare nel cestino?"
          message={`La pratica "${p.tipo_pratica_nome}"${p.cliente ? ` di ${p.cliente.nome} ${p.cliente.cognome}` : ""} verrà spostata nel cestino (recuperabile per 30 giorni).\nIl cliente e i documenti collegati NON verranno eliminati.`}
          confirmLabel="Sposta nel cestino" onConfirm={confirmDelete} onClose={() => setDeleteOpen(false)} />
      )}
      {trasformaOpen && <TrasformaPraticaDialog pratica={p} onClose={() => { setTrasformaOpen(false); refresh(); }} />}
      {cambiaTipoOpen && <CambiaTipoDialog pratica={p} onClose={() => { setCambiaTipoOpen(false); refresh(); }} />}
    </div>
  );
}
